/**
 * 포커스 복귀 도우미 (리뷰 8회차 m41) — 누른 버튼이 사라지거나 다시 그려질 때 포커스가 body 로 떨어지지 않게.
 * 제목처럼 원래 포커스를 받지 않는 요소는 tabindex="-1" 을 붙여 프로그램으로만 옮긴다(Tab 순서에는 들어가지 않는다).
 */

/**
 * 사용자가 마지막으로 조작한 때 (11회차 m55 · Codex 3차 N9).
 * 누르기(pointerdown) · 키(keydown) · 이 도우미가 옮기지 않은 포커스 이동(focusin — 낭독기의 가상 커서, 다른 부품의 focus())를
 * 문서 전체에서 잰다. 늦은 포커스 요청은 요청한 뒤(또는 작업을 시작한 뒤) 이 값이 바뀌면 버린다 — 사용자가 고른 곳을 빼앗지 않게.
 */
let lastUserAt = 0;
/** 이 도우미 · 창 부품이 직접 옮기는 동안 — 그 focusin 은 사용자 조작이 아니다 */
let quiet = false;
const stamp = () => { lastUserAt = performance.now(); };
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', stamp, true);
  document.addEventListener('keydown', stamp, true);
  document.addEventListener('focusin', () => { if (!quiet) stamp(); }, true);
}

/** 작업을 시작한 때 — 처리가 끝난 뒤 포커스를 옮기는 호출부가 `{ since }` 로 넘긴다(처리 중 사용자가 다른 곳을 누르면 옮기지 않는다) */
export function focusStamp(): number {
  return performance.now();
}

/** 프로그램으로 옮기는 포커스 — 사용자 조작으로 세지 않는다(창의 첫 포커스 · 연 곳 복귀 · 이 도우미) */
export function quietFocus(el: HTMLElement): void {
  quiet = true;
  try { el.focus({ preventScroll: true }); } finally { quiet = false; }
}

export function focusEl(el: HTMLElement | null | undefined): boolean {
  if (!el) return false;
  if (!el.matches('a[href], button, input, select, textarea, [tabindex]')) el.setAttribute('tabindex', '-1');
  quietFocus(el);
  return document.activeElement === el;
}

/**
 * 지금 포커스를 받을 수 있는가 — 화면에 붙어 있고, 창이 열려 inert 가 된 본문 안이 아니고, 눌 수 없게(disabled) 되지 않았고, 보인다.
 * 10회차 m43: 창을 닫는 그리기와 inert 해제보다 먼저 포커스를 옮기면 inert 안이라 focus() 가 먹지 않아 body 로 떨어졌다.
 */
export function usable(el: HTMLElement | null | undefined): el is HTMLElement {
  return !!el && el.isConnected && !el.closest('[inert]') && !(el as HTMLButtonElement).disabled && el.getClientRects().length > 0;
}

/** 본문의 첫 제목(h1) — 골격(aria-busy) 안의 임시 제목은 곧 바뀌므로 건너뛴다 */
export function pageHeading(): HTMLElement | null {
  return Array.from(document.querySelectorAll<HTMLElement>('main h1')).find((h) => !h.closest('[aria-busy="true"]')) ?? null;
}

/** 본문의 첫 제목(h1) — 포커스를 둘 곳이 통째로 사라졌을 때 */
export function focusMainHeading(): void {
  focusEl(pageHeading());
}

/** 요청 세대 — 새 요청이 오면 앞 요청은 그만둔다(Codex 3차 N9 ③: 오래된 요청이 새 요청 뒤에 포커스를 다시 가져갔다) */
let gen = 0;

export interface FocusLaterOpts {
  /** 기다릴 횟수(16ms 마다) — 기본 40(약 0.6초), 화면 이동은 늘린다 */
  tries?: number;
  /** 이 때 뒤로 사용자가 조작했으면 옮기지 않는다 — 기본은 요청한 때. 처리가 오래 걸리는 작업은 시작한 때를 넘긴다(focusStamp) */
  since?: number;
  /**
   * 옮길 화면(경로). 주면 그 경로가 될 때까지 기다리고 그 경로에서만 옮긴다(컬렉션 지우기 → 「내 컬렉션」).
   * 안 주면 요청한 화면에서만 — 그사이 다른 화면으로 가면 버린다(N9 ②: 대상이 사라진 뒤 다른 화면 h1 로 갔다).
   */
  route?: string;
  /**
   * 요청한 화면(경로) — 기본은 부르는 때의 경로. 비동기 작업은 **시작한 때의 경로**(startJob().path)를 넘긴다 —
   * 12회차 Codex 4차 N18: 작업 중 다른 화면으로 간 뒤 끝나서 부르면 그때 경로(새 화면)가 잡혀 새 화면 h1 로 옮겼다.
   */
  path?: string;
  /** 옮긴 뒤 화면 안으로 굴린다(가까운 쪽) — 결과 상자처럼 목록 위에 새로 생긴 곳(일괄 처리 막대는 내려가도 따라오는 sticky) */
  scroll?: boolean;
  /** 첫 시도를 곧바로(다음 그리기 전에) — 누른 줄이 곧 사라질 때 남는 이웃으로 미리 옮겨 body 를 거치지 않게 */
  now?: boolean;
}

/**
 * 다시 그린 뒤 고른 요소로 — 없으면 대신할 곳, 그것도 없으면 본문 h1.
 * 고른 요소가 아직 없거나 inert(창이 닫히는 중) · disabled(처리 중) 이면 16ms 마다 다시 본다.
 * 예전에는 setTimeout 0 한 번이라 창의 inert 해제보다 먼저 돌면 body 로 떨어졌다(10회차 m43 · m46).
 * 11회차 m55 · N9: 기다리는 동안 ① 사용자가 누르거나 키를 치거나 다른 곳으로 포커스가 옮겨지면(그 결과 포커스가 어떤 칸에 있으면)
 * ② 새 요청이 오면 ③ 다른 화면으로 가면 그만둔다.
 * 돌려주는 함수를 부르면 취소한다.
 */
export function focusLater(pick: () => HTMLElement | null | undefined, fallback?: () => HTMLElement | null | undefined, opt: number | FocusLaterOpts = {}): () => void {
  const o: FocusLaterOpts = typeof opt === 'number' ? { tries: opt } : opt;
  const tries = o.tries ?? 40;
  const since = o.since ?? performance.now();
  const startPath = o.path ?? window.location.pathname;
  // 시작한 화면을 이미 떠났다 — 요청을 만들지 않는다(앞 요청도 그대로 둔다)
  if (!o.route && startPath !== window.location.pathname) return () => undefined;
  const my = ++gen;
  let n = 0;
  let timer = 0;
  // 사용자가 그사이 조작해 어떤 칸에 포커스가 있으면 건드리지 않는다. 그 칸이 목록에서 빠져 포커스가 body 로 떨어졌거나
  // 빈 곳을 눌렀으면 빼앗을 것이 없다 — 그때는 옮긴다(사용자가 고른 줄이 승인으로 빠진 경우)
  const lost = () => { const a = document.activeElement; return !a || a === document.body || !a.isConnected; };
  const userHolds = () => lastUserAt > since && !lost();
  const onRoute = () => (o.route ? window.location.pathname === o.route : window.location.pathname === startPath);
  const tick = () => {
    if (my !== gen) return;                           // 새 요청이 왔다
    if (!o.route && !onRoute()) return;              // 요청한 화면을 떠났다
    if (!userHolds() && onRoute()) {
      const el = pick();
      if (usable(el) && focusEl(el)) { if (o.scroll) el.scrollIntoView({ block: 'nearest' }); return; }
    }
    if (++n < tries) { timer = window.setTimeout(tick, 16); return; }
    if (userHolds() || !onRoute()) return;            // 사용자가 고른 칸 · 목적 화면이 끝내 안 열림 — 다른 화면 h1 로 가지 않는다
    const fb = fallback?.();
    if (usable(fb) && focusEl(fb)) return;
    focusMainHeading();
  };
  if (o.now) tick(); else timer = window.setTimeout(tick, 0);
  return () => { window.clearTimeout(timer); if (my === gen) gen += 1; };
}
